import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { CATEGORY_KEYS } from "@/lib/categories";
import LocationPicker from "@/components/LocationPicker";
import DateTimeField from "@/components/community/DateTimeField";
import { MapPin, PlusCircle, Flame, Crosshair, Shield } from "lucide-react";

const SWISS_CANTONS = ["AG","AI","AR","BE","BL","BS","FR","GE","GL","GR","JU","LU","NE","NW","OW","SG","SH","SO","SZ","TG","TI","UR","VD","VS","ZG","ZH"];
const COUNTRIES = ["CH","FR","DE","IT","AT"];

export default function SubmitEvent() {
  const { t, tCategory, tCountry } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({
    title: "", description: "", category: "market", country: "CH", canton: "ZH",
    city: "", venue: "", start_date: "", end_date: "", image: "", ticket_url: "",
    price_info: "", organizer_name: "", lat: 46.8182, lng: 8.2275, distance_from_border_km: 0, sabbat: "",
  });

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const inp = "w-full bg-[#181A20] border border-[#272A34] rounded-md px-3 py-2.5 text-sm text-stone-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500";

  const detectLocation = async () => {
    const q = [f.venue, f.city, f.country].filter(Boolean).join(", ");
    if (!f.city) { toast.error(t("field_city")); return; }
    try {
      const res = await api.get("/geocode", { params: { q } });
      setF((p) => ({ ...p, lat: res.data.lat, lng: res.data.lng }));
      toast.success(t("geo_detected"));
    } catch { toast.error(t("geo_failed")); }
  };

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(window.location.origin + "/submit")}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.start_date || !f.end_date) { toast.error(t("field_start")); return; }
    setSaving(true);
    try {
      const payload = { ...f, sabbat: f.sabbat || null, lat: Number(f.lat), lng: Number(f.lng), distance_from_border_km: Number(f.distance_from_border_km),
        start_date: new Date(f.start_date).toISOString(), end_date: new Date(f.end_date).toISOString(),
        canton: f.country === "CH" ? f.canton : null };
      await api.post("/events/submit", payload);
      toast.success(t("submit_success"));
      navigate("/profile");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally { setSaving(false); }
  };

  if (!user) {
    return (
      <div className="max-w-md mx-auto px-6 py-24 text-center">
        <Flame className="w-12 h-12 text-amber-500 mx-auto mb-6" />
        <h1 className="font-serif text-2xl text-amber-100 mb-4">{t("submit_title")}</h1>
        <p className="text-stone-400 mb-6">{t("submit_login")}</p>
        <p className="text-xs text-amber-200/80 mb-8" data-testid="submit-login-moderation-note">{t("submit_moderation_note")}</p>
        <button data-testid="submit-login-btn" onClick={handleLogin} className="bg-amber-600 hover:bg-amber-500 text-stone-950 font-semibold px-6 py-3 rounded-md">{t("login_google")}</button>
      </div>
    );
  }

  return (
    <>
      <Seo title={t("submit_title")} canonicalPath="/submit" />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-12 py-12">
        <div className="flex items-center gap-3 mb-2"><PlusCircle className="w-6 h-6 text-amber-500" /><h1 className="font-serif text-3xl sm:text-4xl uppercase tracking-tight text-amber-100">{t("submit_title")}</h1></div>
        <p className="text-stone-400 mb-6">{t("submit_desc")}</p>
        <div data-testid="submit-moderation-note" className="flex items-start gap-3 mb-10 rounded-lg border border-amber-800/40 bg-amber-950/20 px-4 py-3">
          <Shield className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
          <p className="text-sm text-amber-200/90">{t("submit_moderation_note")}</p>
        </div>

        <form onSubmit={submit} className="space-y-6" data-testid="submit-event-form">
          <Field label={t("field_title")}><input data-testid="field-title" required value={f.title} onChange={(e) => set("title", e.target.value)} className={inp} /></Field>
          <Field label={t("field_description")}><textarea data-testid="field-description" required rows={5} value={f.description} onChange={(e) => set("description", e.target.value)} className={inp} /></Field>

          <div className="grid sm:grid-cols-2 gap-6">
            <Field label={t("filter_category")}>
              <select data-testid="field-category" value={f.category} onChange={(e) => set("category", e.target.value)} className={inp}>
                {CATEGORY_KEYS.map((c) => <option key={c} value={c}>{tCategory(c)}</option>)}
              </select>
            </Field>
            <Field label={t("filter_country")}>
              <select data-testid="field-country" value={f.country} onChange={(e) => set("country", e.target.value)} className={inp}>
                {COUNTRIES.map((c) => <option key={c} value={c}>{tCountry(c)}</option>)}
              </select>
            </Field>
          </div>

          {f.country === "CH" ? (
            <Field label={t("filter_canton")}>
              <select data-testid="field-canton" value={f.canton} onChange={(e) => set("canton", e.target.value)} className={inp}>
                {SWISS_CANTONS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
          ) : (
            <Field label={t("filter_distance")}><input data-testid="field-distance" type="number" min="0" max="100" value={f.distance_from_border_km} onChange={(e) => set("distance_from_border_km", e.target.value)} className={inp} /></Field>
          )}

          <div className="grid sm:grid-cols-2 gap-6">
            <Field label={t("field_city")}><input data-testid="field-city" required value={f.city} onChange={(e) => set("city", e.target.value)} className={inp} /></Field>
            <Field label={t("field_venue")}><input data-testid="field-venue" value={f.venue} onChange={(e) => set("venue", e.target.value)} className={inp} /></Field>
          </div>

          <div className="grid sm:grid-cols-2 gap-6">
            <Field label={t("field_start")}><DateTimeField testId="field-start" value={f.start_date} onChange={(v) => set("start_date", v)} /></Field>
            <Field label={t("field_end")}><DateTimeField testId="field-end" value={f.end_date} onChange={(v) => set("end_date", v)} /></Field>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 font-mono text-xs uppercase tracking-wider text-stone-500"><MapPin className="w-4 h-4 text-amber-600/80" /> {t("geo_pin")}</label>
              <button type="button" data-testid="detect-location-btn" onClick={detectLocation} className="inline-flex items-center gap-1.5 text-xs font-mono text-amber-500 hover:text-amber-400 uppercase tracking-wider"><Crosshair className="w-3.5 h-3.5" /> {t("geo_detect")}</button>
            </div>
            <p className="text-xs text-stone-500" data-testid="geocode-note">{t("geocode_note")}</p>
            <LocationPicker lat={Number(f.lat)} lng={Number(f.lng)} onPick={(lat, lng) => setF((p) => ({ ...p, lat, lng }))} />
            <div className="font-mono text-[11px] text-stone-500">{Number(f.lat).toFixed(4)}, {Number(f.lng).toFixed(4)}</div>
          </div>

          <Field label={t("field_image")}><input data-testid="field-image" value={f.image} onChange={(e) => set("image", e.target.value)} placeholder="https://…" className={inp} /></Field>
          <div className="grid sm:grid-cols-2 gap-6">
            <Field label={t("field_ticket")}><input data-testid="field-ticket" value={f.ticket_url} onChange={(e) => set("ticket_url", e.target.value)} placeholder="https://…" className={inp} /></Field>
            <Field label={t("field_price")}><input data-testid="field-price" value={f.price_info} onChange={(e) => set("price_info", e.target.value)} className={inp} /></Field>
          </div>
          <Field label={t("field_organizer")}><input data-testid="field-organizer" value={f.organizer_name} onChange={(e) => set("organizer_name", e.target.value)} className={inp} /></Field>

          <Field label={t("field_sabbat")}>
            <select data-testid="field-sabbat" value={f.sabbat} onChange={(e) => set("sabbat", e.target.value)} className={inp}>
              <option value="">{t("sabbat_none")}</option>
              {["imbolc", "ostara", "beltane", "litha", "lughnasadh", "mabon", "samhain", "yule"].map((k) => <option key={k} value={k}>{t(`sabbat_${k}`)}</option>)}
            </select>
          </Field>

          <button data-testid="submit-event-btn" disabled={saving} type="submit" className="bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-500 hover:to-amber-600 text-stone-950 font-semibold px-7 py-3.5 rounded-md shadow-lg transition-all disabled:opacity-60 border border-amber-400/30">
            {saving ? "…" : t("submit_send")}
          </button>
        </form>
      </div>
    </>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="block font-mono text-xs uppercase tracking-wider text-stone-500 mb-2">{label}</label>
    {children}
  </div>
);
